import { MouseEvent, useCallback, useState } from 'react';

import { assignAlerts } from '../../api/services/Alerts';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

type AlertIds = number[];

interface IUseBulkAssignAlerts {
  selectedAlertIds: AlertIds;
  handleCheckboxSelection: (event: MouseEvent, id: number, alertIds: AlertIds) => void;
  handleSelectAll: (event: MouseEvent, alerts) => void;
  handleAssignAlerts: ({ assigneeId }) => void;
  isLoading: boolean;
  assignAlertsResponse: string[];
  reset: () => void;
  pruneIds: (ids: AlertIds) => void;
  isCheckAll: boolean;
  setIsCheckAll: (value: boolean) => void;
}

export const useBulkAssignAlerts = (): IUseBulkAssignAlerts => {
  const [selectedAlertIds, setSelectedAlertIds] = useState<AlertIds>([]);
  const [lastSelectedId, setLastSelectedId] = useState<Nullable<number>>();
  const [assignAlertsResponse, setAssignAlertsResponse] = useState<string[]>([]);
  const [isCheckAll, setIsCheckAll] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { addToast } = useToast();
  const { authenticityToken } = useAuth();

  const handleSelectAll = (event: MouseEvent, alerts) => {
    event.stopPropagation();
    setIsCheckAll(!isCheckAll);
    setSelectedAlertIds(alerts.map((n) => n.id));
    if (isCheckAll) {
      setSelectedAlertIds([]);
    }
  };

  const handleCheckboxSelection = (event: MouseEvent, id: number, alertIds: AlertIds) => {
    event.stopPropagation();
    let additionalIdsToCheck: AlertIds = [];

    if (event.nativeEvent.shiftKey && lastSelectedId) {
      let shouldAddToSelectedIds = false;

      additionalIdsToCheck = alertIds
        .map((rideId) => {
          if (rideId === id) {
            shouldAddToSelectedIds = false;

            return null;
          }
          if (shouldAddToSelectedIds) {
            return rideId;
          }
          if (rideId === lastSelectedId) {
            shouldAddToSelectedIds = true;

            return rideId;
          }

          return null;
        })
        .filter(Boolean) as AlertIds;
    }
    if (selectedAlertIds.indexOf(id) !== -1) {
      setSelectedAlertIds(selectedAlertIds.filter((_, rideIndex) => rideIndex !== selectedAlertIds.indexOf(id)));
    } else {
      setLastSelectedId(id);
      setSelectedAlertIds(Array.from(new Set([...selectedAlertIds, ...additionalIdsToCheck, id])));
    }
  };

  const handleAssignAlerts = async ({ assigneeId }) => {
    if (selectedAlertIds.length) {
      setIsLoading(true);

      const assignResponse = await assignAlerts(selectedAlertIds, assigneeId, authenticityToken);

      if (assignResponse.type === 'error') {
        assignResponse.error.errors.forEach((error) => addToast({ severity: 'error', message: error }));
      } else {
        setAssignAlertsResponse(assignResponse.data.messages);
      }
      setIsLoading(false);
    }
  };

  const reset = useCallback(() => {
    setSelectedAlertIds([]);
    setAssignAlertsResponse([]);
  }, []);

  const pruneIds = useCallback((ids: AlertIds) => {
    setSelectedAlertIds((prev) => {
      if (!prev.some((id) => ids.includes(id))) return prev;

      return prev.filter((id) => !ids.includes(id));
    });
  }, []);

  return {
    selectedAlertIds,
    isLoading,
    handleSelectAll,
    handleCheckboxSelection,
    handleAssignAlerts,
    assignAlertsResponse,
    reset,
    pruneIds,
    isCheckAll,
    setIsCheckAll,
  };
};
